import {
	MAX_SEARCH_LENGTH,
	MIN_SEARCH_LENGTH,
	SEARCH_FILTERS,
} from '@features/customers/constants/search.constants';
import type {
	HandleAutocompleteSearch,
	HandleSearchPressed,
} from '@features/customers/interfaces/search.interfaces';
import { ClienteSuite } from '@interfaces/firestore-documents.interfaces';
import { KeyboardArrowDown, Search } from '@mui/icons-material';
import { MenuItem, Skeleton } from '@mui/material';
import clsx from 'clsx';
import {
	handleInputChange,
	handleInputKeyDown,
	handleInputKeyPress,
	handleInputPaste,
	handleSearchOptionClick,
	handleSearchOptionKeyPress,
} from './SearchBar.handlers';
import useSearchBarStyles from './SearchBar.styles';
import SearchBarSelect from './SearchBarSelect';
import { Dispatch, SetStateAction } from 'react';
import SearchFilters from '@features/customers/constants/search-filters.enum';

interface Props {
	data: ClienteSuite[];
	filter: string;
	handleAutocompleteSearch: HandleAutocompleteSearch;
	handleSearchPressed: HandleSearchPressed;
	loading: boolean;
	needAutocomplete: boolean;
	searchText: string;
	setNeedAutocomplete(status: boolean): void;
	verifyText(str: string): boolean;
	handleChangeFilter(e: { target: { value: SetStateAction<SearchFilters> } }): void;
	valueEmail: string;
	setValueEmail: Dispatch<SetStateAction<string>>;
}

const SearchBar: React.FC<Props> = ({
	data,
	filter,
	handleAutocompleteSearch,
	handleSearchPressed,
	loading,
	needAutocomplete,
	searchText,
	setNeedAutocomplete,
	verifyText,
	handleChangeFilter,
	valueEmail,
	setValueEmail,
}) => {
	const classes = useSearchBarStyles();

	return (
		<div className={classes.root}>
			<div className={classes.searchContainer}>
				<div className={classes.inputContainer}>
					<div className={classes.searchIcon}>
						<Search sx={{ height: 18, width: 18 }} />
					</div>
					{filter === 'identificacion' ? (
						<input
							autoComplete="off"
							className={classes.searchInput}
							id="customers-search-input"
							maxLength={MAX_SEARCH_LENGTH}
							onChange={handleInputChange(handleAutocompleteSearch, verifyText)}
							onKeyDown={handleInputKeyDown(needAutocomplete, setNeedAutocomplete)}
							onKeyPress={handleInputKeyPress(handleSearchPressed, verifyText)}
							onPaste={handleInputPaste(setNeedAutocomplete, verifyText)}
							placeholder="Buscar..."
							type="text"
							value={searchText}
						/>
					) : filter === 'email' ? (
						<input
							autoComplete="off"
							className={classes.searchInput}
							id="customers-search-input"
							placeholder="Buscar..."
							type="text"
							value={valueEmail}
							onChange={(e) => setValueEmail(e.target.value)}
							onKeyPress={(event) => {
								if (event.key === 'Enter') {
									window.location.href = `/admin/users?search=${valueEmail}`;
								}
							}}
						/>
					) : null}
				</div>

				<div className={classes.divider} />

				<SearchBarSelect
					IconComponent={KeyboardArrowDown}
					value={filter}
					variant="standard"
					onChange={(e: any) => handleChangeFilter(e)}
				>
					{Object.keys(SEARCH_FILTERS).map((key, i) => (
						<MenuItem key={`search_filter_${key}_${i}`} value={key}>
							{SEARCH_FILTERS[key]}
						</MenuItem>
					))}
				</SearchBarSelect>
			</div>
			{/* Resultados */}
			{searchText.length >= MIN_SEARCH_LENGTH && needAutocomplete && (
				<div className={classes.resultsContainer} id="customers-search-data">
					{loading &&
						Array.from(Array(5)).map((_, i) => (
							<div key={`skeleton_${i + 1}`} style={{ padding: '0 24px' }}>
								<Skeleton variant="text" />
							</div>
						))}
					{!loading && data.length === 0 && (
						<div
							className={classes.dataContainer}
							id="customers-search-data-no-results"
						>
							<div className={classes.dataContainerIcon}>
								<Search />
							</div>
							<span id="customers-search-data-no-results-text">
								No se encontraron resultados relacionados
							</span>
						</div>
					)}
					{!loading && data.length > 0
						? data.map((cliente, i) => (
								<div
									className={clsx(
										classes.dataContainer,
										classes.dataContainerClickable
									)}
									id={`customers-search-data-autocomplete-${cliente.identificacion}`}
									key={`cliente_suite_${i}_${cliente.identificacion}`}
									onClick={handleSearchOptionClick(handleSearchPressed, cliente)}
									onKeyPress={handleSearchOptionKeyPress(
										handleSearchPressed,
										cliente
									)}
									role="button"
									tabIndex={i + 1}
								>
									<div className={classes.dataContainerIcon}>
										<Search />
									</div>
									<span
										dangerouslySetInnerHTML={{
											__html: cliente.identificacion.replaceAll(
												searchText,
												`<span style="font-weight: 700;">${searchText}</span>`
											),
										}}
										id={`customers-search-data-autocomplete-${cliente.identificacion}-text`}
									/>
								</div>
						  ))
						: null}
				</div>
			)}
		</div>
	);
};

export default SearchBar;
